import * as t from 'io-ts';
import {isBoolean, pickBy} from 'lodash';

import {isDevelopment, isMobileBeta, isTest} from '../../helpers/environment/envHelpers';
import {isMobile} from '../../helpers/environment/mobileEnvHelpers';
import {trackFlagRequested} from '../../services/featureFlag/featureFlags';
import {CommonAppSelector} from '../commonAppSelectorTypes';
import {CommonAppState} from '../commonAppState';
import {getCommonCompany, getCompanyHasTicketStatusesFeature} from '../data/company/commonCompanySelectors';
import {
  getHasInboxImprovementsTogglePreferenceEnabled,
  getHasInboxRedesignGaPreferenceEnabled,
} from '../data/preferences/commonPreferencesSelectors';
import {
  getHasInboxRedesignPreferenceEnabled,
  getHasMobileInboxRedesignPreferenceEnabled,
  getStashFeatureFlagOverrides,
} from '../data/stash/stashSelectors';
import {createCommonSelector} from '../helpers/commonReselectHelpers';
import {FeatureFlagsMap} from './featureFlagsState';
import {FeatureFlagsEnum} from './featureFlagsTypes';
import {isVersionedFeatureFlagEnabled, parseVersionedFeatureFlag} from './versionedFeatureFlagHelpers';

/*
 * Common selectors.
 */

function getDynamicFeatureFlags(state: CommonAppState) {
  return state.featureFlags.dynamic;
}
function getRawInitialFeatureFlags(state: CommonAppState) {
  return state.featureFlags.initial;
}

function baseGetFeatureFlagValue(name: string, rawValue: unknown, defaultValue: boolean) {
  const value = isBoolean(rawValue) ? rawValue : defaultValue;

  try {
    trackFlagRequested(name, value);
  } catch {
    // Ignore.
  }

  return value;
}

function baseGetFeatureFlagVariationValue<T>(
  name: FeatureFlagsEnum,
  rawValue: unknown,
  runtimeType: t.Type<T>,
  defaultValue: T,
): T {
  const value = runtimeType.is(rawValue) ? rawValue : defaultValue;

  try {
    trackFlagRequested(name, value);
  } catch {
    // Ignore.
  }

  return value;
}

function makeGetDynamicFeatureFlagRaw(name: string): CommonAppSelector<CommonAppState, unknown> {
  return (state) => getDynamicFeatureFlags(state)[name];
}

export const getHasInternalToolbar = createCommonSelector(
  [makeGetDynamicFeatureFlagRaw(FeatureFlagsEnum.INTERNAL_TOOLBAR)],
  (hasInternalToolbar) => hasInternalToolbar || isDevelopment(),
);

const getFeatureFlagOverrides = createCommonSelector(
  [getStashFeatureFlagOverrides, getHasInternalToolbar],
  (stashFeatureFlagOverrides, hasInternalToolbar) => {
    if (!hasInternalToolbar) {
      // If the user doesn't have access to the internal toolbar, we don't want to override any feature flags because they can't remove them.
      return {};
    }

    return stashFeatureFlagOverrides;
  },
);

export const getInitialFeatureFlags = createCommonSelector(
  [getRawInitialFeatureFlags, getFeatureFlagOverrides],
  (initialFeatureFlags, featureFlagOverrides) => {
    return {...initialFeatureFlags, ...featureFlagOverrides};
  },
);

/**
 * Create a selector that will return a map with only selected featureFlagNames.
 */
export function makeGetInitialFeatureFlagsSubset(featureFlagNames: ReadonlyArray<string>) {
  return createCommonSelector(
    [getInitialFeatureFlags],
    (featureFlags): FeatureFlagsMap =>
      pickBy(featureFlags, (value, featureFlagKey) => featureFlagNames.includes(featureFlagKey)),
  );
}

/** Creates a selector that will query the value of a feature flag with real time updates. */
export function makeGetHasDynamicFeatureFlag(name: FeatureFlagsEnum, defaultValue = false) {
  return createCommonSelector(
    [makeGetDynamicFeatureFlagRaw(name), getFeatureFlagOverrides],
    (rawValue, flagOverrides) => {
      if (isBoolean(flagOverrides[name])) {
        return flagOverrides[name];
      }

      return baseGetFeatureFlagValue(name, rawValue, defaultValue);
    },
  );
}

export function makeGetHasVersionedFeatureFlag(name: FeatureFlagsEnum) {
  return createCommonSelector(
    [makeGetDynamicFeatureFlagRaw(name), getFeatureFlagOverrides],
    (rawValue, flagOverrides) => {
      if (isBoolean(flagOverrides[name])) {
        return flagOverrides[name];
      }

      const flag = parseVersionedFeatureFlag(rawValue);
      const isEnabled = isVersionedFeatureFlagEnabled(flag);

      try {
        trackFlagRequested(name, isEnabled);
      } catch {
        // Ignore.
      }

      return isEnabled;
    },
  );
}

/** Creates a selector that will query the value of a feature flag with real time updates. */
export function makeGetHasDynamicFeatureFlagVariation<T>(
  name: FeatureFlagsEnum,
  runtimeType: t.Type<T>,
  defaultValue: T,
) {
  return createCommonSelector([makeGetDynamicFeatureFlagRaw(name)], (rawValue) =>
    baseGetFeatureFlagVariationValue(name, rawValue, runtimeType, defaultValue),
  );
}

function makeGetInitialFeatureFlagRaw(name: string): CommonAppSelector<CommonAppState, unknown> {
  return (state) => getRawInitialFeatureFlags(state)[name];
}

/** Creators a selector that will query the initial value of a feature flag. */
export function makeGetHasInitialFeatureFlag(name: FeatureFlagsEnum, defaultValue = false) {
  return createCommonSelector(
    [makeGetInitialFeatureFlagRaw(name), getFeatureFlagOverrides],
    (rawValue, flagOverrides) => {
      if (isBoolean(flagOverrides[name])) {
        return flagOverrides[name];
      }

      return baseGetFeatureFlagValue(name, rawValue, defaultValue);
    },
  );
}

/** Creates a selector that will query the initial value of a feature flag. */
export function makeGetHasInitialFeatureFlagVariation<T>(
  name: FeatureFlagsEnum,
  runtimeType: t.Type<T>,
  defaultValue: T,
) {
  return createCommonSelector([makeGetInitialFeatureFlagRaw(name)], (rawValue) =>
    baseGetFeatureFlagVariationValue(name, rawValue, runtimeType, defaultValue),
  );
}

/*
 * Feature Flags.
 */

// Prior to sandbox-v3 we used a different scheme for tracking demo progress, this timestamp is hardcoded so
// customers don't lose progress.
export const getHasSandboxV3Progress = createCommonSelector(
  [getCommonCompany],
  (company) => company.createdAt >= 1701477000000,
);

export const getHasChatbotPreview = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.CHATBOT_PREVIEW, false);
export const getHasChatbotHistoryFF = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.CHATBOT_HISTORY, false);

export const getHasTablePasteIgnorePasteAsText = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.TABLE_PASTE_IGNORE_PASTEASTEXT_PREFERENCE,
  false,
);

// Admin setup guide is available to new companies created after Sat Feb 03 2024 06:00:00 UTC.
// You're probably looking for getAvailableSetupGuide.
export const getHasAdminSetupGuide = createCommonSelector(
  [getCommonCompany],
  (company) => company.createdAt >= 1706940000000,
);

export const getHasAnalyticsWorkloadTour = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.ANALYTICS_WORKLOAD_TOUR,
);

export const getHasAnalyticsDynamicTableColumns = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.ANALYTICS_DYNAMIC_TABLE_COLUMNS,
  false,
);

const EsToChMigrationStatus = t.union([
  t.literal('"not_started"'), // values have double quotes
  t.literal('"in_progress"'),
  t.literal('"completed"'),
]);

export const getAnalyticsEsToChDataMigrationStatus = makeGetHasDynamicFeatureFlagVariation(
  FeatureFlagsEnum.ANALYTICS_ES_TO_CH_DATA_MIGRATION,
  EsToChMigrationStatus,
  '"not_started"',
);

export const getHasAnalyticsAiReportsV1 = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.ANALYTICS_AI_REPORTS_V1,
  false,
);

export const getHasSortP2BySlaTimerFF = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SORT_P2_BY_SLA_TIMER,
  false,
);

// After this date we use company.preferences.activationPreferences.adminOnboardingResources
// as the source of truth for the Admin Wizard's completion state. We're using the timestamp to
// avoid a backfill.
export const getHasOnboardingPrefilledWorkspaceV3Progress = createCommonSelector(
  [getCommonCompany],
  (company) => company.createdAt >= 1709683200000,
);

export const getHasQuickInsertTable = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.QUICK_INSERT_TABLE,
  isDevelopment(),
);

// Store the value in Redux for live updates.
export const getHasOfflineCache = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.OFFLINE_CONVERSATION_CACHE,
  isDevelopment(),
);

export const getHasNewConversationContentPrefetch = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.NEW_P3_PREFETCH,
  false,
);
export const getHasNewPrefetchV3 = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.NEW_PREFETCH_V3, false);
export const getHasMoreP2Prefetch = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.MORE_P2_PREFETCH, false);
export const getHasServiceWorkerBootCachePatching = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SW_BOOT_CACHE_PATCHING,
  false,
);

export const getHasOldDesktopVersionBanner = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.OLD_DESKTOP_VERSION_BANNER,
  isDevelopment(),
);

export const getHasIncorrectArchitectureBanner = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.INCORRECT_ARCHITECTURE_BANNER_ENABLED,
  false,
);
export const getShouldHideAuditTrail = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.HIDE_AUDIT_TRAIL, false);

export const getIsChatWidgetUnavailable = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SUPPORT_CHAT_FALLBACK,
  false,
);
export const getHasServiceWorker = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.SERVICE_WORKER, false);

export const getIsLegacyPlanMigrationCompany = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.LEGACY_PLAN_MIGRATION,
  false,
);

export const getHasDatadogRum = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.DATADOG_RUM, false);

export const getHasDragAndDropLogging = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.LOG_DRAG_AND_DROP_OPERATIONS,
  isDevelopment(),
);

export const getHasRuleEditorHighlightErrors = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.RULE_EDITOR_HIGHLIGHT_ERRORS,
  false,
);

export const getHasP2CellsRedesignPhase2 = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.P2_CELLS_REDESIGN_PHASE_2,
);

export const getHasP2CellsRedesignPhase2Improvements = createCommonSelector(
  [getHasInboxImprovementsTogglePreferenceEnabled, getHasP2CellsRedesignPhase2],
  (hasInboxImprovementsTogglePreferenceEnabled, hasP2CellsRedesignPhase2) =>
    Boolean(hasP2CellsRedesignPhase2 && hasInboxImprovementsTogglePreferenceEnabled),
);

export const getHasP2FilterMultiselectRedesign = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.P2_FILTER_MULTISELECT_REDESIGN,
);

const getHasP2FiltersRedesignFlag = makeGetHasVersionedFeatureFlag(FeatureFlagsEnum.P2_FILTERS_REDESIGN);

export const getHasP2FiltersRedesign = createCommonSelector(
  [getHasInboxImprovementsTogglePreferenceEnabled, getHasP2FiltersRedesignFlag],
  (hasInboxImprovementsTogglePreferenceEnabled, hasP2FiltersRedesignFlag) =>
    Boolean(hasP2FiltersRedesignFlag && hasInboxImprovementsTogglePreferenceEnabled),
);

export const getHasP2ServerBoundary = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P2_SERVER_BOUNDARY,
  false,
);

export const getHasGranularOfflineIndication = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.GRANULAR_OFFLINE_INDICATION,
  false,
);

export const getHasRulesDryRun = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.RULES_DRY_RUN,
  isDevelopment(),
);

export const getHasConditionsTokenEditor = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CONDITIONS_TOKEN_EDITOR,
  isDevelopment(),
);

export const getHasContactsOtherNamesDialog = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CONTACTS_OTHER_NAMES_DIALOG,
  isDevelopment(),
);

export const getHasPerformanceDebugLogging = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.PERFORMANCE_DEBUG_LOGGING,
  false,
);

export const getHasPerformanceMetricLoggingDesktop = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.PERFORMANCE_METRIC_LOGGING_DESKTOP,
  false,
);

export const getHasPerformanceMetricLoggingMobile = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.PERFORMANCE_METRIC_LOGGING_MOBILE,
  false,
);

export const getHasPolicyViolationWarning = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.POLICY_VIOLATION_WARNING,
);

export const getHasRtlLanguageSupport = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.RTL_LANGUAGE_SUPPORT,
  false,
);

export const getHasSendgridSubuser = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SENDGRID_DNS_SUBUSER,
  false,
);

export const getHasP4PopOutAnyPlugin = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P4_POP_OUT_ANY_PLUGIN,
  false,
);

export const getHasShowFrontChatVisitorUrlRule = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SHOW_FRONT_CHAT_VISITOR_URL_RULE,
  false,
);

export const getHasDeepPerformanceInstrumentation = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.DEEP_PERFORMANCE_INSTRUMENTATION,
  false,
);

export const getAutocompleteMoreResults = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.AUTOCOMPLETE_MORE_RESULTS,
  false,
);

export const getHasReact18StrictMode = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.REACT_18_STRICT_MODE,
  false,
);

export const getHasQualBotSettingEnabled = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.PRE_CHAT_FORM_ENABLED,
  false,
);

export const getShouldHideTeamTagCounters = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.HIDE_TEAM_TAG_COUNTERS,
  false,
);

export const getShouldHideAssignedConversations = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.HIDE_ASSIGNED_CONVERSATIONS,
  false,
);

export const getHasCustomViews = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.HAS_CUSTOM_VIEWS, false);

export const getHasSelectorPerformanceLogging = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SELECTOR_PERFORMANCE_LOGGING,
  false,
);

export const getHasApplicationServerRelay = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CUSTOM_PLUGIN_RELAY,
);

export const getHasPreventPinTeamTags = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.PREVENT_PIN_TEAM_TAGS);

const EnglishStemmerType = t.union([
  t.literal('"MINIMAL_ENGLISH"'),
  t.literal('"PORTER2"'),
  t.literal('"KSTEM"'),
]);
export type EnglishStemmerType = t.TypeOf<typeof EnglishStemmerType>;

export const getEnglishStemmer = makeGetHasDynamicFeatureFlagVariation(
  FeatureFlagsEnum.SEARCH_ENGLISH_STEMMER,
  EnglishStemmerType,
  '"MINIMAL_ENGLISH"',
);

export const getHasDataGroupInstrumentation = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.DATA_GROUP_INSTRUMENTATION,
  false,
);

export const getHasRepeatFormatting = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.REPEAT_FORMATTING,
  isDevelopment(),
);

export const getHasIsCreatingFromOrphanEvent = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.CREATE_CALENDAR_EVENT_VERSION_FROM_ORPHAN,
  false,
);

export const getHasIsCreatingFromOrphanEventOffice365 = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.CREATE_CALENDAR_EVENT_VERSION_FROM_ORPHAN_OFFICE_365,
  false,
);

export const getHasInternalAiAnswersAnnouncement = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.INTERNAL_AI_ANSWERS_ANNOUNCEMENT,
);

export const getHasMobileCalendarWrite = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.MOBILE_CALENDAR_WRITE_GA,
);

export const getHasEnforceChannelsTeammateRatio = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.ENFORCE_CHANNELS_TEAMMATE_RATIO,
  false,
);

export const getHasIncreasedLensesInboxLimit = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.LENSES_INCREASED_INBOX_LIMIT,
  false,
);

export const getHasChannelsRecommendations = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CHANNELS_RECOMMENDATIONS,
  false,
);

export const getHasChatOnboardingImprovements = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CHAT_ONBOARDING_IMPROVEMENTS,
  isDevelopment(),
);

export const getHasMarkdownInEmailComposer = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SHOW_MARKDOWN_EMAIL_COMPOSER,
  false,
);

const getHasCustomTicketStatusesFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.TICKETING_CUSTOM_STATUSES,
  false,
);

export const getHasCustomTicketStatusesFeature = createCommonSelector(
  [getHasCustomTicketStatusesFlag, getCompanyHasTicketStatusesFeature],
  (hasCustomTicketStatusesFlag, hasCompanyFeature) => hasCustomTicketStatusesFlag && hasCompanyFeature,
);

export const getHasCustomTicketStatusesWorkflowsFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.TICKETING_CUSTOM_STATUSES_WORKFLOWS,
  false,
);

export const getHasSplitSingleThreadedConvos = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SPLIT_SINGLE_THREADED_CONVOS,
  false,
);

export const getHasCompanyEnforcedSignaturesFeature = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.COMPANY_ENFORCED_SIGNATURES,
  isDevelopment(),
);

export const getHasAiComposeV2FloatingButton = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.AI_COMPOSE_V2_FLOATING_BUTTON,
  isDevelopment(),
);

export const getHasP4StartGuideHidden = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P4_START_GUIDE_HIDDEN,
  false,
);

export const getHasGlgIndividualRuleWarning = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.GLG_INDIVIDUAL_RULE_WARNING,
  isDevelopment(),
);

export const getHasChatbotDynamicVariableBetweenTimeOperation = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.CHATBOT_DYNAMIC_VARIABLE_BETWEEN_TIME_OPERATION,
  false,
);

export const getHasLinkedDiscussions = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.LINKED_DISCUSSIONS,
  false,
);

export const getAiTaggingTagLimit = makeGetHasInitialFeatureFlagVariation(
  FeatureFlagsEnum.AI_TAGGING_TAG_LIMIT,
  t.number,
  10,
);

export const getHasHidePrivateCategoriesP4 = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.HIDE_PRIVATE_CATEGORIES_P4,
  false,
);

export const getRequestTraceTag = makeGetHasDynamicFeatureFlagVariation(
  FeatureFlagsEnum.REQUEST_TRACE_TAG,
  t.string,
  '',
);

export const getHasCellsOfflineCache = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CELLS_OFFLINE_CACHE,
  false,
);

export const getHasCellsOfflineCacheReporting = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CELLS_OFFLINE_CACHE_REPORTING,
  false,
);

export const getHasMessageTranslation = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.MESSAGE_TRANSLATION,
  isDevelopment(),
);

export const getHasConversationSynopsis = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CONVERSATION_SYNOPSIS,
  false,
);

export const getHasCustomerPortal = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CUSTOMER_PORTAL,
  isDevelopment(),
);

export const getHasConversationUndoBannerRedesign = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CONVERSATION_UNDO_BANNER_REDESIGN,
  false,
);

export const getHasJumpToBeginningBanner = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.JUMP_TO_BEGINNING_BANNER,
  false,
);

export const getHasAiDisableComposeWhenTooShort = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_DISABLE_COMPOSE_WHEN_TOO_SHORT,
  isDevelopment(),
);

export const getHasAiAutopilotReport = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_AUTOPILOT_REPORT,
  isDevelopment(),
);

export const getHasAiRepliesPage = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_REPLIES_PAGE,
  isDevelopment(),
);

export const getHasKnowledgeBaseEndUserAuth = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.KNOWLEDGE_BASE_END_USER_AUTH,
  false,
);

export const getHasJiraPrebuiltConnectors = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.JIRA_PREBUILT_CONNECTORS,
  isDevelopment(),
);

export const getHasKnowledgeBaseContactFormAdditionalFields = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.KNOWLEDGE_BASE_CONTACT_FORM_ADDITIONAL_FIELDS,
  isDevelopment(),
);

export const getHasKnowledgeBaseArticleReactions = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.KNOWLEDGE_BASE_ARTICLE_REACTIONS,
  isDevelopment(),
);

export const getHasKnowledgeBaseStaleArticlesV2 = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.KNOWLEDGE_BASE_STALE_ARTICLES_V2,
  isDevelopment(),
);

export const getOnboardingChatDelay = makeGetHasInitialFeatureFlagVariation(
  FeatureFlagsEnum.ONBOARDING_CHAT_AUTOMATION,
  t.number,
  0,
);

export const getHasSkipQuoteHtmlIfQuoteIsNotIncluded = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SKIP_QUOTE_HTML_IF_QUOTE_IS_NOT_INCLUDED,
  false,
);

export const getHasMacroDraft = makeGetHasVersionedFeatureFlag(FeatureFlagsEnum.MACRO_DRAFT);

export const getHasIncidentManagement = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.INCIDENT_MANAGEMENT);

export const getHasPreloadedConversationsFeature = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.PRELOADED_CONVERSATIONS,
  false,
);

export const getHasRingCentralReleaseAnnouncement = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.RINGCENTRAL_RELEASE_ANNOUNCEMENT,
  false,
);

export const getHasSlackReleaseAnnouncement = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SLACK_RELEASE_ANNOUNCEMENT,
  false,
);

export const getHasMobileComposerDraftFix = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.MOBILE_COMPOSER_DRAFT_FIX,
  isDevelopment(),
);

export const getHasMobilePrefetcher = makeGetHasVersionedFeatureFlag(FeatureFlagsEnum.MOBILE_PREFETCHER);

export const getHasMobileImprovedEmailRendering = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.MOBILE_IMPROVED_EMAIL_RENDERING,
);

export const getHasEditPublishedApplication = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.EDIT_PUBLISHED_APPLICATIONS,
  false,
);

export const getHasApplicationConnectorOutputPrettyJson = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.APPLICATION_CONNECTOR_OUTPUT_PRETTY_JSON,
  false,
);

export const getHasApplicationConnectorMultiRequest = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.APPLICATION_CONNECTOR_MULTI_REQUEST,
  false,
);

export const getHasApplicationConnectorDevelopers = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.APPLICATION_CONNECTOR_DEVELOPERS,
  false,
);

export const getHasAppRequestPagination = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.APP_REQUEST_PAGINATION,
  false,
);

export const getHasAppRequestSearch = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.APP_REQUEST_SEARCH,
  false,
);

export const getHasApiDashboard = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.API_DASHBOARD, false);

export const getHasSearchEmptyBehavior = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SEARCH_EMPTY_BEHAVIOR,
  false,
);

export const getHasSingleContactSync = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SINGLE_CONTACT_SYNC,
  false,
);

export const getHasSearchLocationResetExperiment = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SEARCH_LOCATION_RESET_EXPERIMENT,
);

export const getHasQuickConnectChannels = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.QUICK_CONNECT_CHANNELS,
  false,
);

export const getHasAiAutopilotBilling = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.AI_AUTOPILOT_BILLING,
  isDevelopment(),
);

export const getHasSharedActions = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.SHARED_ACTIONS, false);
export const getHasInternalTasks = makeGetHasVersionedFeatureFlag(FeatureFlagsEnum.INTERNAL_TASKS);

export const getHasInternalTasksStandalone = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.INTERNAL_TASKS_STANDALONE,
);

export const getHasMacroButtonInComposerToolbar = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.MACRO_BUTTON_IN_COMPOSER_TOOLBAR,
  false,
);

const RTriggeredSuggestionConfiguration = t.partial({
  disableCooldownPeriod: t.boolean,
  disableDismissPermanently: t.boolean,
  enableNewCompaniesFilter: t.boolean,
  enableCustomersFilter: t.boolean,
  shouldLog: t.boolean,
});

export interface TriggeredSuggestionConfiguration
  extends t.TypeOf<typeof RTriggeredSuggestionConfiguration> {}

const RTriggeredSuggestionsConfiguration = t.partial({
  liveDashboardSuggestion: t.intersection([
    RTriggeredSuggestionConfiguration,
    t.type({
      minimumUnassignedConversations: t.number,
    }),
  ]),
  teamChannelSuggestion: RTriggeredSuggestionConfiguration,
  enableAiCopilotSuggestion: RTriggeredSuggestionConfiguration,
  configureAiWorkspaceSettingsSuggestion: RTriggeredSuggestionConfiguration,
  configureAiCopilotKnowledgeBaseSuggestion: RTriggeredSuggestionConfiguration,
  discardSuggestedReplyKnowledgeBaseSuggestion: RTriggeredSuggestionConfiguration,
});

export interface TriggeredSuggestionsConfiguration
  extends t.TypeOf<typeof RTriggeredSuggestionsConfiguration> {}

export const getTriggeredSuggestionsConfiguration = makeGetHasInitialFeatureFlagVariation(
  FeatureFlagsEnum.TRIGGERED_SUGGESTIONS,
  RTriggeredSuggestionsConfiguration,
  {},
);

export const getHasAnalyticsChatbotsAiAnswersCategoriesTable = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.ANALYTICS_CHATBOTS_AI_ANSWERS_CATEGORIES_TABLE,
  false,
);

export const getHasAnalyticsDeprecatedReports = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.ANALYTICS_DEPRECATED_REPORTS,
  false,
);

export const getHasCustomerPortalFormPreview = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CUSTOMER_PORTAL_FORM_PREVIEW,
  false,
);

export const getHasClosedConversations = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.CLOSED_CONVERSATIONS,
);

export const getHasOffice365ImporterDeprecation = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.OFFICE_365_IMPORTER_DEPRECATION,
  false,
);

export const getHasFrontChatPoweredBy = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.FRONT_CHAT_POWERED_BY,
  false,
);

export const getHasInboxRedesignGaFlag = makeGetHasVersionedFeatureFlag(FeatureFlagsEnum.INBOX_REDESIGN_GA);

export const getHasInboxRedesignForceEnable = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.INBOX_REDESIGN_FORCE_ENABLE_VERSIONED,
);

export const getHasMobileInboxRedesignToggle = createCommonSelector(
  [makeGetHasVersionedFeatureFlag(FeatureFlagsEnum.MOBILE_INBOX_REDESIGN_TOGGLE)],
  (hasRedesignToggle) => {
    if (!isMobile()) {
      return false;
    }

    if (isDevelopment()) {
      return true;
    }

    if (hasRedesignToggle) {
      return isMobileBeta();
    }

    return false;
  },
);

const getHasMobileInboxRedesign = createCommonSelector(
  [getHasMobileInboxRedesignPreferenceEnabled, getHasMobileInboxRedesignToggle],
  (hasMobileInboxRedesignPreferenceEnabled, hasMobileInboxRedesignToggle) => {
    if (!isMobile()) {
      return false;
    }

    return hasMobileInboxRedesignPreferenceEnabled && hasMobileInboxRedesignToggle;
  },
);

export const getHasInboxRedesign = createCommonSelector(
  [
    getHasInboxRedesignPreferenceEnabled,
    getHasInboxRedesignGaFlag,
    getHasInboxRedesignGaPreferenceEnabled,
    getHasInboxRedesignForceEnable,
    getHasMobileInboxRedesign,
  ],
  (
    hasInboxRedesignPreferenceEnabled,
    hasInboxRedesignGaFlag,
    hasInboxRedesignGaPreferenceEnabled,
    hasInboxRedesignForceEnabled,
    hasMobileInboxRedesign,
  ) => {
    if (isMobile()) {
      return hasMobileInboxRedesign;
    }

    if (isTest()) {
      return true;
    }

    if (hasInboxRedesignForceEnabled) {
      return true;
    }

    return hasInboxRedesignGaFlag
      ? hasInboxRedesignGaPreferenceEnabled ?? hasInboxRedesignPreferenceEnabled
      : hasInboxRedesignPreferenceEnabled;
  },
);

export const getHasInboxRedesignP3SubjectTwoLines = createCommonSelector(
  [
    makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.INBOX_REDESIGN_P3_SUBJECT_TWO_LINES, false),
    getHasInboxRedesign,
  ],
  (hasInboxRedesignP3SubjectTwoLines, hasInboxRedesign) =>
    hasInboxRedesign && hasInboxRedesignP3SubjectTwoLines,
);

export const getHasStandalonePortal = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.STANDALONE_PORTAL, false);

export const getHasTicketStatusGroups = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.TICKET_STATUS_GROUPS);

export const getHasApplicationOauth2PasswordGrantType = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.APPLICATION_OAUTH2_PASSWORD_GRANT_TYPE,
);

export const getHasAdminCommandCenterP0EntryPoint = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.ADMIN_COMMAND_CENTER_P_0_ENTRY_POINT,
  isDevelopment(),
);

export const getHasSearchNegatedFilters = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SEARCH_NEGATED_FILTERS,
);

export const getHasDraftChecksum = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.DRAFT_CHECKSUM);

export const getHasSetupGuideDiscoverAIFlag = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SETUP_GUIDE_DISCOVER_AI,
  false,
);

export const getHasChatAiDispatcher = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CHAT_AI_DISPATCHER,
  false,
);

export const getHasChatSelfServeExperience = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CHAT_SELF_SERVE_EXPERIENCE_FOR_FRONT_USERS,
  false,
);

const getHasInboxRedesignAnnouncementFlag = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.INBOX_REDESIGN_ANNOUNCEMENT,
  false,
);

export const getIsEligibleForInboxRedesignGaAnnouncementAgain = createCommonSelector(
  [
    getHasInboxRedesignAnnouncementFlag,
    getHasInboxRedesignGaFlag,
    getHasInboxRedesignGaPreferenceEnabled,
    getHasInboxRedesignForceEnable,
  ],
  (
    hasInboxRedesignAnnouncementFlag,
    hasInboxRedesignGaFlag,
    hasInboxRedesignGaPreferenceEnabled,
    hasInboxRedesignForceEnable,
  ) =>
    Boolean(
      hasInboxRedesignAnnouncementFlag &&
        hasInboxRedesignGaFlag &&
        // show it again to people who we are force enabling it
        // for (meaning they had the preference disabled)
        !hasInboxRedesignGaPreferenceEnabled &&
        hasInboxRedesignForceEnable,
    ),
);

export const getHasSkipArchiveWhenChangingOtherAssignees = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SKIP_ARCHIVE_WHEN_CHANGING_OTHER_ASSIGNEES,
  true,
);
export const getHasSupportAtScaleAnnouncement = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SUPPORT_AT_SCALE_ANNOUNCEMENT,
  false,
);

export const getHasAiComposeAssistant = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_COMPOSE_ASSISTANT,
  isDevelopment(),
);

export const getHasAiComposeAssistantDev = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_COMPOSE_ASSISTANT_DEV,
  isDevelopment(),
);

export const getHasRingCentral = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.RINGCENTRAL, false);

export const getHasWorkspacePermissionsFF = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.WORKSPACE_PERMISSIONS,
  false,
);

const getHasP1EditWorkspaceRedesignFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P1_EDIT_WORKSPACE_REDESIGN,
);

export const getHasP1EditWorkspaceRedesign = createCommonSelector(
  [getHasInboxImprovementsTogglePreferenceEnabled, getHasP1EditWorkspaceRedesignFlag],
  (hasInboxImprovementsTogglePreferenceEnabled, hasP1EditWorkspaceRedesignFlag) =>
    Boolean(hasP1EditWorkspaceRedesignFlag && hasInboxImprovementsTogglePreferenceEnabled),
);

const getHasP1ReorderWorkspaceSectionsFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P1_REORDER_WORKSPACE_SECTIONS,
  false,
);

export const getHasP1ReorderWorkspaceSections = createCommonSelector(
  [getHasInboxImprovementsTogglePreferenceEnabled, getHasP1ReorderWorkspaceSectionsFlag],
  (hasInboxImprovementsTogglePreferenceEnabled, hasP1ReorderWorkspaceSectionsFlag) =>
    Boolean(hasP1ReorderWorkspaceSectionsFlag && hasInboxImprovementsTogglePreferenceEnabled),
);

const getHasP1WorkspaceCustomizationTourFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P1_WORKSPACE_CUSTOMIZATION_TOUR,
  false,
);

export const getHasP1WorkspaceCustomizationTour = createCommonSelector(
  [getHasInboxImprovementsTogglePreferenceEnabled, getHasP1WorkspaceCustomizationTourFlag],
  (hasInboxImprovementsTogglePreferenceEnabled, hasP1WorkspaceCustomizationTourFlag) =>
    Boolean(hasP1WorkspaceCustomizationTourFlag && hasInboxImprovementsTogglePreferenceEnabled),
);

export const getHasAiEchoConfidenceScore = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_ECHO_CONFIDENCE_SCORE,
  false,
);

export const getHasAiEchoesHubMergeModal = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_ECHOES_HUB_MERGE_MODAL,
  false,
);

export const getHasAiFactInvalidation = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.AI_FACT_INVALIDATION,
  false,
);

export const getHasSearchNegatedQuickFilters = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SEARCH_NEGATED_QUICK_FILTERS,
  false,
);

export const getHasMessageTemplatePreviews = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.MESSAGE_TEMPLATE_PREVIEWS,
  false,
);

export const getHasLegacySlackAccess = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.LEGACY_SLACK_ACCESS,
  false,
);

export const getHasSettingsCopilot = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.SETTINGS_COPILOT, false);

export const getHasSmartQAInWorkspace = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SMART_QA_IN_WORKSPACE,
  isDevelopment(),
);

export const getHasInboxIconInCell = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.INBOX_ICON_IN_CELL, false);

export const getHasAiPromptInExpressions = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_PROMPT_IN_EXPRESSIONS,
  false,
);

export const getHasChatWidgetRefresh = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.CHAT_WIDGET_REFRESH,
  false,
);

export const getHasSettingsDarkMode = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.SETTINGS_DARK_MODE,
  false,
);

export const getHasOutboundRuleValidation = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.OUTBOUND_RULE_VALIDATION,
  false,
);

export const getHasUserSurvey = makeGetHasDynamicFeatureFlag(FeatureFlagsEnum.USER_SURVEY, false);

export const getHasDropdownRedesign = makeGetHasVersionedFeatureFlag(FeatureFlagsEnum.DROPDOWN_REDESIGN);

export const getHasSettingsCopilotStreaming = makeGetHasVersionedFeatureFlag(
  FeatureFlagsEnum.SETTINGS_COPILOT_STREAMING,
);

export const getHasSettingsCopilotNodeSteps = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SETTINGS_COPILOT_NODE_STEPS,
  false,
);

export const getHasFeatureHelpRequestCta = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.FEATURE_HELP_REQUEST_CTA,
  false,
);

export const getHasMentionsInEmailComposer = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.MENTIONS_IN_EMAIL_COMPOSER,
  false,
);

export const getHasAiExtractText = makeGetHasInitialFeatureFlag(FeatureFlagsEnum.AI_EXTRACT_TEXT, false);

export const getHasAiGenerateWithInstructions = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.AI_GENERATE_WITH_INSTRUCTIONS,
  false,
);

export const getHasInboxImprovementsToggle = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.INBOX_IMPROVEMENT_TOGGLE,
  false,
);

export const getHasBranchingRulesTester = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.BRANCHING_RULES_TESTER,
  isDevelopment(),
);

export const getHasAskCopilot = makeGetHasVersionedFeatureFlag(FeatureFlagsEnum.ASK_COPILOT);

const getHasP3HeaderRedesignFeedbackFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P3_HEADER_REDESIGN_FEEDBACK,
  false,
);

export const getHasP3HeaderRedesignFeedback = createCommonSelector(
  [getHasInboxImprovementsTogglePreferenceEnabled, getHasP3HeaderRedesignFeedbackFlag],
  (hasInboxImprovementsTogglePreferenceEnabled, hasP3HeaderRedesignFeedbackFlag) =>
    Boolean(hasP3HeaderRedesignFeedbackFlag && hasInboxImprovementsTogglePreferenceEnabled),
);

export const getHasP3HeaderExpandTip = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P3_HEADER_EXPAND_TIP,
  false,
);

export const getHasApplicationChannelAtMentions = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.APPLICATION_CHANNEL_AT_MENTIONS,
  false,
);

export const getHasMassChannelCreate = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.MASS_CHANNEL_CREATE,
  false,
);

export const getHasSlackChannelFlowImprovements = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.SLACK_CHANNEL_FLOW_IMPROVEMENTS,
  false,
);

export const getHasPrivateAiKnowledgeSources = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.PRIVATE_AI_KNOWLEDGE_SOURCES,
  false,
);

const getHasP1RedesignAddButtonOnEditModeFlag = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.P1_REDESIGN_ADD_BUTTON_ON_EDIT_MODE,
  false,
);

export const getHasP1RedesignAddButtonOnEditMode = createCommonSelector(
  [getHasInboxImprovementsTogglePreferenceEnabled, getHasP1RedesignAddButtonOnEditModeFlag],
  (hasInboxImprovementsTogglePreferenceEnabled, hasP1RedesignAddButtonOnEditModeFlag) =>
    Boolean(hasP1RedesignAddButtonOnEditModeFlag && hasInboxImprovementsTogglePreferenceEnabled),
);

export const getIsEligibleForInboxImprovementsLaunch = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.INBOX_IMPROVEMENTS_LAUNCH_ANNOUNCEMENT,
  false,
);

export const getHasFrontUnrwappedAnnouncement = makeGetHasDynamicFeatureFlag(
  FeatureFlagsEnum.FRONT_UNWRAPPED_ANNOUNCEMENT,
  false,
);

export const getHasAppRequestNodeInMacro = makeGetHasInitialFeatureFlag(
  FeatureFlagsEnum.APP_REQUEST_NODE_IN_MACRO,
  false,
);
